<template>
    <DsModal
        class="web-form-submitted-goal-modal"
        :is-open="isOpen"
        show-close
        size="med"
        :title="webFormTitle"
        @close="close"
    >
        <template #header>
            <DsFilledButton @click="editOrCreateWebForm">
                <template #default>
                    {{ actionButtonLabel }}
                </template>
                <template #trailing>
                    <DsIcon name="external-link" />
                </template>
            </DsFilledButton>
        </template>

        <template #default>
            <div class="goal-summary">
                <div class="goal-summary-label">
                    {{ $t('campaign.webForm.submitted.summaryLabel') }}
                </div>

                <div class="goal-summary-text">
                    {{ $t('campaign.webForm.submitted.summaryText') }}
                </div>
            </div>

            <div v-if="isConfigured">
                <div class="goal-configuration">
                    <div class="configuration-section">
                        <div class="configuration-section-header">
                            <div class="configuration-section-label">
                                {{ $t('campaign.webForm.submitted.urlLabel') }}
                            </div>
                            <DsTextButton
                                v-show="!isEditingUrl"
                                id="edit-url"
                                class="configuration-button"
                                @click="editUrl"
                            >
                                {{ $t('campaign.webForm.submitted.editUrl') }}
                            </DsTextButton>
                        </div>
                        <div class="configuration-section-text">
                            {{ isEditingUrl ? $t('campaign.webForm.submitted.urlTextEdit') : $t('campaign.webForm.submitted.urlText') }}
                        </div>
                        <div v-if="!isEditingUrl" class="configuration-url-section-body">
                            <DsInputField v-model="webFormUrl" readonly />
                            <DsOutlineButton
                                class="copy-button"
                                @click="copyUrl"
                            >
                                <template #leading>
                                    <DsIcon name="copy" />
                                </template>
                                <template #default>
                                    {{ $t('campaign.webForm.submitted.copyLink') }}
                                </template>
                            </DsOutlineButton>
                        </div>
                        <div v-else>
                            <div class="configuration-url-section-body-edit">
                                <DsInputField v-model="webFormBaseUrl" readonly />
                                <DsInputField id="web-form-handle" v-model="webFormUrlHandle" />
                            </div>
                            <div class="configuration-url-section-actions">
                                <DsFilledButton
                                    id="url-save-button"
                                    @click="saveUrl"
                                >
                                    {{ $t('campaign.webForm.submitted.save') }}
                                </DsFilledButton>
                                <DsTextButton
                                    id="url-cancel-button"
                                    @click="cancelUrl"
                                >
                                    {{ $t('campaign.webForm.submitted.cancel') }}
                                </DsTextButton>
                            </div>
                        </div>
                    </div>
                    <div class="configuration-section">
                        <div class="configuration-section-header">
                            <div class="configuration-section-label">
                                {{ $t('campaign.webForm.submitted.addToWebsiteLabel') }}
                            </div>
                        </div>
                        <div class="configuration-section-text">
                            {{ $t('campaign.webForm.submitted.addToWebsiteText') }}
                        </div>
                        <div class="configuration-section-body">
                            <DsTextAreaField v-model="webFormJavaScript" readonly class="embed-code" />
                            <DsOutlineButton
                                class="copy-button"
                                @click="copyCode"
                            >
                                <template #leading>
                                    <DsIcon name="copy" />
                                </template>
                                <template #default>
                                    {{ $t('campaign.webForm.submitted.copyCode') }}
                                </template>
                            </DsOutlineButton>
                        </div>
                    </div>
                </div>

                <div class="goal-warning">
                    <div class="goal-warning-icon">
                        <DsIcon name="alert-triangle" />
                    </div>

                    <div class="goal-warning-text">
                        {{ $t('campaign.webForm.submitted.warning') }}
                        <div>
                            <DsFilledButton
                                class="goal-warning-button"
                                as="a"
                                href="https://help.keap.com/help/create-a-web-form"
                                target="_blank"
                                trailing-icon="external-link"
                            >
                                <div>
                                    {{ $t('campaign.webForm.submitted.tipButtonLabel') }}
                                </div>
                            </DsFilledButton>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="!isConfigured">
                <div class="goal-tip">
                    <div class="goal-tip-icon">
                        <DsIcon name="info-circle" />
                    </div>

                    <div class="goal-tip-text">
                        <span>{{ $t('campaign.webForm.submitted.tipLabel') }}</span>
                        {{ $t('campaign.webForm.submitted.tip') }}
                        <div>
                            <DsFilledButton
                                class="goal-tip-button"
                                as="a"
                                href="https://help.infusionsoft.com/help/create-a-web-form"
                                target="_blank"
                                trailing-icon="external-link"
                            >
                                <div>
                                    {{ $t('campaign.webForm.submitted.tipButtonLabel') }}
                                </div>
                            </DsFilledButton>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </DsModal>
</template>

<script>
import {
    createWebForm,
    getWebFormSettings,
    saveCustomLink,
    saveWebFormSettings,
} from './web.form.submitted.api';

const EVENT_READY_TOGGLE_CHANGED = 'webform-toggle-changed';
const EVENT_RENAME = 'webform-rename';
const EVENT_EDITOR_LOADED = 'webform-editor-loaded';
const EVENT_EDITOR_SAVE = 'webform-editor-save';
const EVENT_SETTINGS_SAVE = 'webform-settings-save';
const EVENT_TYPE_NAME_CHANGED = 'webform-name-changed';
const EVENT_TYPE_READY_TOGGLE_CHANGED = 'webform-ready-toggle-changed';

export default {
    props: {
        cell: {
            type: Object,
            default: () => ({
                value: {
                    webformId: '0',
                    ready: 0,
                },
            }),
        },

        isOpen: {
            type: Boolean,
            required: true,
        },

        funnelId: {
            type: Number,
            required: true,
        },
    },

    emits: ['close-modal', 'update-cell-value', 'update-cell-name'],

    data() {
        return {
            loading: false,
            webFormConfiguration: {},
            webFormId: '0',
            editorInstance: null,
            isReady: false,
            isEditingUrl: false,
            webFormUrlHandle: '',
        };
    },

    computed: {
        isConfigured() {
            return this.webFormId !== '0';
        },

        webFormTitle() {
            return this.cell.value?.name ?? this.$t('campaign.webForm.submitted.title');
        },

        webFormUrl() {
            return this.webFormConfiguration?.webFormUrl ?? '';
        },

        webFormBaseUrl() {
            if (this.webFormUrl) {
                return this.webFormUrl.substring(0, this.webFormUrl.lastIndexOf('/') + 1);
            }

            return '';
        },

        webFormCustomLink() {
            if (this.webFormUrl) {
                return this.webFormUrl.substring(this.webFormUrl.lastIndexOf('/') + 1, this.webFormUrl.length);
            }

            return '';
        },

        webFormJavaScript() {
            return this.webFormConfiguration?.webFormJavaScript ?? '';
        },

        actionButtonLabel() {
            return this.isConfigured ? this.$t('campaign.webForm.submitted.edit') : this.$t('campaign.webForm.submitted.create');
        },
    },

    mounted() {
        this.load();
        this.addEditorEventListeners();
    },

    unmounted() {
        this.removeEditorEventListeners();
    },

    methods: {
        addEditorEventListeners() {
            window.addEventListener(EVENT_READY_TOGGLE_CHANGED, this.readyToggleChanged);
            window.addEventListener(EVENT_EDITOR_LOADED, this.editorLoaded);
            window.addEventListener(EVENT_RENAME, this.editorRename);
            window.addEventListener(EVENT_EDITOR_SAVE, this.editorSave);
            window.addEventListener(EVENT_SETTINGS_SAVE, this.editorSaveSettings);
        },

        removeEditorEventListeners() {
            window.removeEventListener(EVENT_READY_TOGGLE_CHANGED, this.readyToggleChanged);
            window.removeEventListener(EVENT_EDITOR_LOADED, this.editorLoaded);
            window.removeEventListener(EVENT_RENAME, this.editorRename);
            window.removeEventListener(EVENT_EDITOR_SAVE, this.editorSave);
            window.removeEventListener(EVENT_SETTINGS_SAVE, this.editorSaveSettings);
        },

        readyToggleChanged() {
            this.isReady = !this.isReady;

            this.saveCell();
        },

        async load() {
            try {
                this.loading = true;

                this.loadCellValue();

                await this.getWebFormSettings();
            } catch (error) {
                const message = `${this.$t('campaign.webForm.submitted.errorLoadingSettings')} ${error}`;

                this.$toast({ message });
            } finally {
                this.loading = false;
            }
        },

        loadCellValue() {
            const cell = { ...this.cell };

            cell.value = cell.value || {};

            const webFormId = cell.value.webformId ?? '0';

            this.webFormId = webFormId.toString().replace('L', '');

            this.isReady = this.cell.value?.ready === '1' ?? false;
        },

        saveCell() {
            const cell = { ...this.cell };

            cell.value = cell.value || {};
            cell.value.webformId = this.webFormId;

            this.$emit('update-cell-value', { cell, isReady: this.isReady, preventClose: true });
        },

        copyUrl() {
            this.$copyText(this.webFormUrl);
            this.$toast({ message: this.$t('campaign.webForm.submitted.urlCopied') });
        },

        copyCode() {
            this.$copyText(this.webFormJavaScript);
            this.$toast({ message: this.$t('campaign.webForm.submitted.codeCopied') });
        },

        close() {
            if (this.editorInstance && !this.editorInstance.closed) {
                this.editorInstance.focus();
            } else {
                if (this.isConfigured) {
                    this.editorSave();
                }

                this.$emit('close-modal');
            }
        },

        async getWebFormSettings() {
            if (this.isConfigured) {
                this.webFormConfiguration = await getWebFormSettings(this.webFormId);
            }
        },

        async editOrCreateWebForm() {
            try {
                if (!this.isConfigured) {
                    this.webFormId = await this.createWebForm();
                    this.isReady = true;
                    await this.getWebFormSettings();
                }
                this.editWebForm();
            } catch (error) {
                const message = `${this.$t('campaign.webForm.submitted.errorCreate')} ${error}`;

                this.$error({ message });
            }
        },

        async createWebForm() {
            return createWebForm(this.webFormTitle, this.funnelId);
        },

        editWebForm() {
            if (this.editorInstance && !this.editorInstance.closed) {
                this.editorInstance.focus();
            } else {
                const editorUrl = `/app/webformFunnel/standaloneEditor?contentType=webform&parentId=${this.webFormId}`;

                this.editorInstance = window.open(editorUrl, '_blank');
            }
        },

        dispatchEditorEvent(eventType, detail = {}) {
            if (this.editorInstance) {
                const message = {
                    eventType,
                    ...detail,
                };

                this.editorInstance.postMessage(message, '*');
            }
        },

        editorLoaded() {
            this.dispatchEditorEvent(EVENT_TYPE_READY_TOGGLE_CHANGED, { ready: this.isReady });
            this.dispatchEditorEvent(EVENT_TYPE_NAME_CHANGED, { name: this.webFormTitle });
        },

        editorRename(event) {
            const newName = event.detail.name;

            if (newName) {
                const cell = { ...this.cell };

                cell.value = cell.value || {};
                cell.value.webformId = this.webFormId;
                cell.value.name = newName;

                this.$emit('update-cell-name', { cell });
            }

            this.dispatchEditorEvent(EVENT_TYPE_NAME_CHANGED, { name: this.webFormTitle });
        },

        editorSave() {
            this.saveCell();
        },

        async editorSaveSettings(event) {
            const settings = event.detail;

            if (settings) {
                try {
                    await saveWebFormSettings(this.webFormId, this.funnelId, settings);
                } catch (error) {
                    const message = `${this.$t('campaign.webForm.submitted.errorSavingSettings')} ${error}`;

                    this.$error({ message });
                }
            }
        },

        editUrl() {
            if (this.webFormCustomLink) {
                this.webFormUrlHandle = this.webFormCustomLink;
                this.isEditingUrl = true;
            }
        },

        async saveUrl() {
            if (this.webFormUrlHandle) {
                try {
                    await saveCustomLink(this.webFormId, this.webFormTitle, this.webFormUrlHandle);

                    await this.getWebFormSettings();

                    this.isEditingUrl = false;
                    this.webFormUrlHandle = '';
                } catch (error) {
                    const message = `${this.$t('campaign.webForm.submitted.errorSavingHandle')} ${error}`;

                    this.$toast({ message });
                }
            } else {
                this.$toast({ message: this.$t('campaign.webForm.submitted.errorHandleEmpty') });
            }
        },

        cancelUrl() {
            this.webFormUrlHandle = '';
            this.isEditingUrl = false;
        },
    },
};
</script>

<style lang="scss" scoped>

div.web-form-submitted-goal-modal ::v-deep {
    div.modal-body {
        padding: 0;
    }

    .goal-tip  {
        margin-top: $spacing-200;
    }

    div.configuration-section-body > div > textarea {
        font-family: Lucida Console,Monaco,monospace;
    }
}

.goal-summary-text {
    margin-top:     $spacing-050;
    line-height:    $line-height-lg;
}

.goal-summary-label {
    font-size:  $font-size-xs;
    color:      $color-ink-800;
}

.goal-configuration {
    margin: $spacing-300 0 0 0;
}

.goal-tip {
    display:            flex;
    padding:            $spacing-150;
    border-radius:      px-to-rem(8);
    flex-wrap:          wrap;
    justify-items:      left;
    background-color:   $color-blue-050;
}

.goal-tip-icon {
    flex:   6%;
    color:  $color-blue-300;
}

.goal-tip-text {
    margin-top: $spacing-025;
    flex:       94%;
    color:      $color-ink-900;
}

.goal-tip-button {
    height:             px-to-rem(32);
    margin-top:         $spacing-100;
    padding:            $spacing-100 $spacing-150 $spacing-100 $spacing-100;
    border-radius:      px-to-rem(8);
    color:              $color-ink-900;
    background-color:   $color-ink-100;
}

.goal-warning {
    display:            flex;
    padding:            $spacing-150;
    border-radius:      px-to-rem(8);
    flex-wrap:          wrap;
    justify-items:      left;
    background-color:   $color-orange-050;
}

.goal-warning-icon {
    flex:   6%;
    color:  $color-orange-500;
}

.goal-warning-text {
    margin-top: $spacing-025;
    flex:       94%;
    color:      $color-ink-900;
}

.goal-warning-button {
    height:             px-to-rem(32);
    margin-top:         $spacing-100;
    padding:            $spacing-100 $spacing-150 $spacing-100 $spacing-100;
    border-radius:      px-to-rem(8);
    color:              $color-ink-900;
    background-color:   $color-ink-100;
}

.configuration-section-header {
    display:        flex;
    flex-wrap:      wrap;
    justify-items:  left;
    padding-bottom: $spacing-300;
    position: relative;
}

.configuration-button {
    position: absolute;
    right: 0;
    top: 0;
    text-align: end;
}

.configuration-url-section-body {
    display: grid;
    grid-template-columns: 75% 23%;
    border-bottom: px-to-rem(0.5) solid $color-gray-200;
    margin-bottom: $spacing-250;
}

.configuration-url-section-body-edit {
    display: grid;
    grid-template-columns: 66% 34%;
}

.configuration-url-section-actions {
    text-align: right;
    border-bottom: px-to-rem(0.5) solid $color-gray-200;
    margin-bottom: $spacing-250;
}

.configuration-section-label {
    font-size: $font-size-sm;
    color:     $color-ink-800;
}

.configuration-section-text {
    margin-bottom: $spacing-250;
}

button.button.copy-button.outline.with-leading {
    margin-bottom: $spacing-300;
}

div.configuration-url-section-body > button {
    margin-left: $spacing-125;
}

div.configuration-url-section-actions > button {
    margin: 0 0 $spacing-250 $spacing-125;
    width: px-to-rem(90);
}

.embed-code {
    width: px-to-rem(445);
}

</style>
